import { useQuery } from '@tanstack/react-query';

interface StatWithUnit {
  value: number;
  unit: string;
  timestamp?: string;
}

interface NotableEvent {
  type: 'record' | 'streak' | 'warning';
  title: string;
  value: string;
  timestamp: string;
}

interface Comparison {
  change: number;
  percentChange: number;
  direction: 'up' | 'down' | 'flat';
}

export interface InsightsData {
  period: {
    start: string;
    end: string;
    label: string;
  };
  statistics: {
    avgDailyDemand: StatWithUnit | null;
    peakDemand: StatWithUnit | null;
    totalRenewableGeneration: StatWithUnit | null;
    renewableShare: StatWithUnit | null;
    avgOutageDuration: StatWithUnit | null;
    totalCustomersAffected: StatWithUnit | null;
    outageDays: StatWithUnit | null;
  };
  notable: NotableEvent[];
  comparisons: {
    vsPreviousPeriod: {
      renewableShare: Comparison | null;
      outageCount: Comparison | null;
      avgDemand: Comparison | null;
      avgDailyDemand: Comparison | null;
      peakDemand: Comparison | null;
      totalCustomersAffected: Comparison | null;
    };
  };
}

export function useInsights(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['insights', startDate, endDate],
    queryFn: async (): Promise<InsightsData> => {
      const params = new URLSearchParams({ start: startDate, end: endDate });
      const res = await fetch(`/api/insights?${params}`);
      if (!res.ok) throw new Error('Failed to fetch insights');
      return res.json();
    },
    staleTime: 60000,
    refetchInterval: 300000,
  });
}
